import styled from '@emotion/styled'
import { Link } from 'gatsby'
import { ReactNode } from 'react'

import { Color, theme } from '../../constants'
import { usePersistedState } from '../../hooks/usePersistedState'
import { Avatar } from '../Avatar'
import { Clickable } from '../Clickable'
import { Div } from '../Div'
import { Flex } from '../Flex'
import { Icon, IconName } from '../Icon'
import { Text } from '../Text'

const Label = styled(Text)<{ first?: boolean }>(({ first }) => ({
  fontSize: 13,
  fontWeight: theme.font.weight.medium,
  color: Color.DARK_GREY,
  marginBottom: theme.spacing[1.5],
  marginTop: first ? undefined : theme.spacing[3],
  marginLeft: theme.spacing['2'],
}))

const TriangleWrapper = styled.svg<{ isOpen: boolean }>(({ isOpen }) => ({
  display: isOpen ? 'block' : 'none',
  transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
  transition: 'transform 0.15s ease-in-out',
}))

const Triangle = (props: { isOpen: boolean }) => {
  return (
    <TriangleWrapper
      width={6}
      height={7}
      fill="#8A8F98"
      viewBox="0 0 6 7"
      className="triangle"
      {...props}
    >
      <path d="M5.25 3.20126C5.58333 3.39371 5.58333 3.87484 5.25 4.06729L0.749999 6.66537C0.416666 6.85782 -2.38418e-07 6.61725 -2.38418e-07 6.23235L-2.38418e-07 1.0362C-2.38418e-07 0.651301 0.416666 0.410738 0.75 0.603188L5.25 3.20126Z" />
    </TriangleWrapper>
  )
}

const NotificationBadge = (props: { count?: number }) => {
  if (!props.count) return null
  return (
    <Div
      styles={{
        color: Color.WHITE,
        fontWeight: theme.font.weight.bold,
        background: Color.RED,
        borderRadius: theme.border.radius.normal,
        marginLeft: 'auto',
        fontSize: 12,
        px: 1.5,
        py: 0.5,
      }}
    >
      {props.count > 99 ? '99+' : props.count}
    </Div>
  )
}

const MenuItemContent = (props: {
  icon?: IconName | 'Initials'
  isOpen?: boolean
  selected?: boolean
  isToggable?: boolean
  label: string
  notificationCount?: number
}) => {
  return (
    <Flex gap={2} styles={{ alignItems: 'center', minHeight: 28, py: 1 }}>
      {props.icon && (
        <Div styles={{ minWidth: 20 }}>
          {props.icon === 'Initials' ? (
            <Avatar size="xs" squared name={props.label} />
          ) : (
            <Icon
              opacity={props.selected ? 1 : 0.7}
              color={Color.DARKER_GREY}
              design="solid"
              name={props.icon}
              size="sm"
              margin="auto"
            />
          )}
        </Div>
      )}
      <Text fontSize={14} fontWeight={theme.font.weight.legacy[500]}>
        {props.label}
      </Text>
      {props.isToggable && <Triangle isOpen={!!props.isOpen} />}
      <NotificationBadge count={props.notificationCount} />
    </Flex>
  )
}

const MenuItemRow = styled.div<{ selected?: boolean; nested?: boolean }>(
  ({ selected, nested }) => ({
    textDecoration: 'none',
    borderRadius: theme.border.radius.normal,
    background: selected ? Color.MEDIUM_GREY : 'transparent',
    margin: 0,
    '&:hover': {
      color: Color.DARKER_GREY,
      background: Color.MEDIUM_GREY,
      '.triangle': {
        display: 'block',
      },
    },
    a: {
      userSelect: 'none',
      color: Color.DARKER_GREY,
      textDecoration: 'none',
    },
    listStyle: 'none',
    paddingLeft: nested ? theme.spacing['3.5'] : theme.spacing['2'],
    paddingRight: theme.spacing[1],
  }),
)

const TogglableGroup = (props: {
  group: string
  icon: IconName | 'Initials'
  notificationCount?: number
  label: string
  children: ReactNode
}) => {
  const [isOpen, setIsOpen] = usePersistedState(
    `${props.group}-menu-item`,
    false,
  )
  return (
    <Flex col gap={0.5}>
      <MenuItemRow>
        <Clickable
          onClick={() => setIsOpen((curr) => !curr)}
          styles={{ border: 'none', width: '100%' }}
        >
          <MenuItemContent
            icon={props.icon}
            isOpen={isOpen}
            isToggable
            label={props.label}
            notificationCount={props.notificationCount}
          />
        </Clickable>
      </MenuItemRow>
      {isOpen && props.children}
    </Flex>
  )
}

const ItemGroup = (props: {
  icon: IconName
  notificationCount?: number
  label: string
  to: string
  children: ReactNode
  nested?: boolean
  selected?: boolean
}) => {
  return (
    <Flex col gap={0.5}>
      <MenuItemRow nested={props.nested} selected={props.selected}>
        <Link to={props.to} color={Color.BLACK}>
          <MenuItemContent
            icon={props.icon}
            label={props.label}
            notificationCount={props.notificationCount}
            selected={props.selected}
          />
        </Link>
      </MenuItemRow>
      <Flex gap={0.5} styles={{ ml: props.nested ? 3.5 : 2 }}>
        <Div styles={{ pl: '9px', pr: '8px' }}>
          <Div
            styles={{
              opacity: 0.2,
              width: '1.5px',
              height: '100%',
              backgroundColor: Color.DARKER_GREY,
            }}
          />
        </Div>
        <Flex
          gap={0.5}
          col
          styles={{
            m: 0,
            width: '100%',
          }}
          as="ul"
        >
          {props.children}
        </Flex>
      </Flex>
    </Flex>
  )
}

const Item = (props: {
  icon?: IconName | 'Initials'
  notificationCount?: number
  label: string
  to: string
  nested?: boolean
  selected?: boolean
  hasPermission?: boolean
  lastLevelPath?: boolean
}) => {
  return (
    <MenuItemRow nested={props.nested} selected={props.selected}>
      <Link to={props.to} color={Color.BLACK}>
        <MenuItemContent
          icon={props.icon}
          label={props.label}
          notificationCount={props.notificationCount}
          selected={props.selected}
        />
      </Link>
    </MenuItemRow>
  )
}

const SidebarNav = {
  TogglableGroup,
  ItemGroup,
  Item,
  Label,
}

export { SidebarNav }
